import * as React from 'react'
import { createBrowserRouter, Navigate, useParams, useSearchParams } from 'react-router-dom'
import DiagSpinner from '../components/diagnostics/DiagSpinner'
import { RequireAuth, RequireRole } from './ProtectedRoute'
import { lazyWithReload } from './lazyWithReload'
import RouteError from './RouteError'
import { MOVED_TO_UPDATES } from './movedPaths'
import { MovedToUpdates } from './movedToUpdates'

// Lazy load pages for code splitting
const Login = lazyWithReload(() => import('../pages/auth/Login'))
const AuthCallback = lazyWithReload(() => import('../pages/auth/AuthCallback'))
const Unauthorized = lazyWithReload(() => import('../pages/auth/Unauthorized'))
const Layout = lazyWithReload(() => import('../components/layout/Layout'))

// Devices (shared across roles)
const Devices = lazyWithReload(() => import('../pages/devices/Devices'))

// Crashes & Errors (Plan C orchestrators)
const Crashes = lazyWithReload(() => import('../pages/crashes/Crashes'))
const Errors = lazyWithReload(() => import('../pages/errors/Errors'))

// Developer detail pages (kept — Plan C does not touch detail pages)
const CrashDetail = lazyWithReload(() => import('../pages/developer/CrashDetail'))
const ErrorDetail = lazyWithReload(() => import('../pages/developer/ErrorDetail'))
const SymbolizedLogRedirect = lazyWithReload(
  () => import('../pages/errors/SymbolizedLogRedirect'),
)

// Releases & Firmware (Plan F.2)
const Releases = lazyWithReload(() => import('../pages/releases/Releases'))
const ReleaseDetail = lazyWithReload(() => import('../pages/releases/ReleaseDetail'))
const ReleaseFailuresPage = lazyWithReload(
  () => import('../pages/releases/ReleaseFailuresPage'),
)
const ActivityPage = lazyWithReload(() => import('../pages/releases/ActivityPage'))
const Firmware = lazyWithReload(() => import('../pages/firmware/Firmware'))
const PackageDetail = lazyWithReload(() => import('../pages/firmware/PackageDetail'))
const UploadWizard = lazyWithReload(() => import('../pages/firmware/UploadWizard'))

// Analytics pages
const Analytics = lazyWithReload(() => import('../pages/analytics/Analytics'))

// Landing (`/` index route) — role-aware; see Landing.tsx for the mapping.
const Landing = lazyWithReload(() => import('./Landing'))

// Support pages
const SupportSearch = lazyWithReload(() => import('../pages/support/SupportSearch'))
const DeviceDiagnostics = lazyWithReload(() => import('../pages/support/DeviceDiagnostics'))

// Home pages
const HomeList = lazyWithReload(() => import('../pages/home/HomeList'))
const HomeDetail = lazyWithReload(() => import('../pages/home/HomeDetail'))

// Cohorts pages (Phase C)
const CohortsList = lazyWithReload(() => import('../pages/cohorts/CohortsList'))
const CohortCreate = lazyWithReload(() => import('../pages/cohorts/CohortCreate'))
const CohortDetail = lazyWithReload(() => import('../pages/cohorts/CohortDetail'))

// Fleet OTA health dashboard (P0.1a — wayfinding, FE-only)
const Fleet = lazyWithReload(() => import('../pages/fleet/Fleet'))

// Webhooks + Rings admin pages (Phase G — OTA webhooks observability + ring catalog)
const WebhooksList = lazyWithReload(() => import('../pages/admin/WebhooksList'))
const WebhookDeliveries = lazyWithReload(() => import('../pages/admin/WebhookDeliveries'))
const RingsList = lazyWithReload(() => import('../pages/admin/RingsList'))

// Display pages
const TvWallPage = lazyWithReload(() => import('../pages/display/TvWallPage'))

// Help pages (P0.3 — OTA glossary; available to all authenticated roles)
const Glossary = lazyWithReload(() => import('../pages/help/Glossary'))

// Audit log (P0.6 — operator audit of OTA admin writes; developer + admin)
const AuditLog = lazyWithReload(() => import('../pages/audit/AuditLog'))

// Plan B: /devices/:deviceId redirects to /support/diagnostics/:deviceId.
// The legacy DeviceDetail.tsx is removed in Task F12.
// eslint-disable-next-line react-refresh/only-export-components -- single-component helper co-located with router config; splitting into its own file would just churn imports.
function DeviceIdRedirect() {
  const { deviceId } = useParams<{ deviceId: string }>()
  return <Navigate replace to={`/support/diagnostics/${deviceId}`} />
}

// Plan C: /error-stats, /product-crashes, /product-errors fold into the
// scope-aware /crashes and /errors orchestrators. Pre-existing bookmarks
// or external links carrying ?productId=... continue to land on the right
// scope; absent productId, they default to fleet scope.
// eslint-disable-next-line react-refresh/only-export-components -- single-component helper co-located with router config; splitting into its own file would just churn imports.
function ErrorStatsRedirect() {
  const [params] = useSearchParams()
  const productId = params.get('productId')
  const target = productId
    ? `/errors?scope=product&id=${productId}`
    : '/errors?scope=fleet'
  return <Navigate to={target} replace />
}

// eslint-disable-next-line react-refresh/only-export-components -- single-component helper co-located with router config; splitting into its own file would just churn imports.
function ProductCrashesRedirect() {
  const [params] = useSearchParams()
  const productId = params.get('productId')
  const target = productId
    ? `/crashes?scope=product&id=${productId}`
    : '/crashes?scope=fleet'
  return <Navigate to={target} replace />
}

// eslint-disable-next-line react-refresh/only-export-components -- single-component helper co-located with router config; splitting into its own file would just churn imports.
function ProductErrorsRedirect() {
  const [params] = useSearchParams()
  const productId = params.get('productId')
  const target = productId
    ? `/errors?scope=product&id=${productId}`
    : '/errors?scope=fleet'
  return <Navigate to={target} replace />
}

/**
 * Application router configuration
 * Uses React Router v7 with lazy loading and role-based access control
 */
export const router = createBrowserRouter([
  // Public routes
  {
    errorElement: <RouteError />,
    path: '/login',
    element: <Login />,
  },
  {
    errorElement: <RouteError />,
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    errorElement: <RouteError />,
    path: '/unauthorized',
    element: <Unauthorized />,
  },

  // TV Wall - full-screen display route (no sidebar/header)
  //
  // Role-gated (not just RequireAuth): TvWallView renders unredacted owner
  // name + home name straight from the diagnostics endpoint. Same four roles
  // as /support/diagnostics/:id, which serves that data everywhere else.
  {
    errorElement: <RouteError />,
    path: '/display/device/:deviceId',
    element: (
      <RequireAuth>
        <RequireRole allow={['developer', 'admin', 'support_agent', 'business_analyst']}>
          <React.Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DiagSpinner size="large" /></div>}>
            <TvWallPage />
          </React.Suspense>
        </RequireRole>
      </RequireAuth>
    ),
  },

  // Dev-only primitives playground - bypasses Layout so the document scrolls
  // and `fullPage` screenshots capture the entire page. The lazy `import()` is
  // declared inside this guard so Vite tree-shakes the chunk in production
  // builds (where neither DEV nor the contract-build flag is true).
  ...(import.meta.env.DEV || import.meta.env.VITE_CONTRACT_BUILD === 'true'
    ? [
        {
          path: '/dev/primitives',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const PrimitivesPlayground = lazyWithReload(
                    () => import('../pages/dev/PrimitivesPlayground'),
                  )
                  return <PrimitivesPlayground />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
        {
          path: '/dev/devices',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const DevicesFixture = lazyWithReload(
                    () => import('../pages/dev/DevicesFixture'),
                  )
                  return <DevicesFixture />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
        {
          path: '/dev/crashes',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const CrashesFixture = lazyWithReload(
                    () => import('../pages/dev/CrashesFixture'),
                  )
                  return <CrashesFixture />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
        {
          path: '/dev/errors',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const ErrorsFixture = lazyWithReload(
                    () => import('../pages/dev/ErrorsFixture'),
                  )
                  return <ErrorsFixture />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
        {
          path: '/dev/analytics',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const AnalyticsFixture = lazyWithReload(
                    () => import('../pages/dev/AnalyticsFixture'),
                  )
                  return <AnalyticsFixture />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
        {
          path: '/dev/release-detail',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const ReleaseDetailPlayground = lazyWithReload(
                    () => import('../pages/dev/ReleaseDetailPlayground'),
                  )
                  return <ReleaseDetailPlayground />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
        {
          path: '/dev/diagnostics/:deviceId',
          element: (
            <RequireAuth>
              <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                {(() => {
                  const DiagnosticsFixture = lazyWithReload(
                    () => import('../pages/dev/DiagnosticsFixture'),
                  )
                  return <DiagnosticsFixture />
                })()}
              </React.Suspense>
            </RequireAuth>
          ),
        },
      ]
    : []),

  // Protected routes (require authentication)
  {
    errorElement: <RouteError />,
    path: '/',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      // Index route — role-aware landing (Landing.tsx). NOT gated with
      // RequireRole: see Landing.tsx's decision note for why.
      {
        index: true,
        element: <Landing />,
      },

      // Devices (shared across all four staff roles, plus external_viewer —
      // spec §3.3)
      {
        path: 'devices',
        element: (
          <RequireRole
            allow={[
              'developer',
              'admin',
              'support_agent',
              'business_analyst',
              'external_viewer',
            ]}
          >
            <Devices />
          </RequireRole>
        ),
      },
      {
        path: 'devices/:deviceId',
        element: <DeviceIdRedirect />,
      },
      {
        path: 'crashes',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <Crashes />
          </RequireRole>
        ),
      },
      {
        path: 'crashes/:deviceId/:timestamp',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <CrashDetail />
          </RequireRole>
        ),
      },
      {
        path: 'errors',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <Errors />
          </RequireRole>
        ),
      },
      {
        // Retired 2026-09-05 — the symbolized log is now the Tail view inside
        // /errors. Kept as a redirect because four surfaces link here and the URL
        // has been shareable for months; see SymbolizedLogRedirect.
        path: 'errors/:deviceId/symbolized',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <SymbolizedLogRedirect />
          </RequireRole>
        ),
      },
      {
        path: 'errors/:deviceId/:errorId',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <ErrorDetail />
          </RequireRole>
        ),
      },
      // Overview and exact legacy page redirect; fleet API paths remain unchanged.
      { path: 'fleet', element: <MovedToUpdates /> },
      {
        path: 'updates/overview',
        element: (
          <RequireRole allow={['developer', 'admin', 'business_analyst']}>
            <Fleet />
          </RequireRole>
        ),
      },

      // Releases routes (Plan F.2)
      {
        path: 'updates/releases',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <Releases />
          </RequireRole>
        ),
      },
      {
        path: 'updates/releases/:id',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <ReleaseDetail />
          </RequireRole>
        ),
      },
      {
        path: 'updates/releases/:id/failures',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <ReleaseFailuresPage />
          </RequireRole>
        ),
      },
      {
        path: 'updates/releases/:id/activity',
        element: (
          <RequireRole allow={['developer', 'admin', 'support_agent']}>
            <ActivityPage />
          </RequireRole>
        ),
      },

      // Firmware routes (Plan F.2)
      {
        path: 'updates/builds',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <Firmware />
          </RequireRole>
        ),
      },
      {
        path: 'updates/builds/packages/:id',
        element: (
          <RequireRole allow={['developer', 'admin']}>
            <PackageDetail />
          </RequireRole>
        ),
      },
      {
        path: 'updates/builds/upload',
        element: (
          <RequireRole allow={['admin']}>
            <UploadWizard />
          </RequireRole>
        ),
      },

      // Cohorts routes (Phase C — OTA cohort management)
      {
        path: 'updates/groups',
        element: (
          <RequireRole allow={['admin', 'developer']}>
            <CohortsList />
          </RequireRole>
        ),
      },
      {
        path: 'updates/groups/new',
        element: (
          <RequireRole allow={['admin']}>
            <CohortCreate />
          </RequireRole>
        ),
      },
      {
        path: 'updates/groups/:id',
        element: (
          <RequireRole allow={['admin', 'developer']}>
            <CohortDetail />
          </RequireRole>
        ),
      },

      // Webhooks + Rings routes (Phase G — admin-only OTA observability/admin)
      {
        path: 'updates/settings/notifications',
        element: (
          <RequireRole allow={['admin']}>
            <WebhooksList />
          </RequireRole>
        ),
      },
      {
        path: 'updates/settings/notifications/:id',
        element: (
          <RequireRole allow={['admin']}>
            <WebhookDeliveries />
          </RequireRole>
        ),
      },
      {
        path: 'updates/settings',
        element: (
          <RequireRole allow={['admin']}>
            <RingsList />
          </RequireRole>
        ),
      },

      // The Updates area (2026-10-26): the old addresses keep working and land on the new ones.
      ...MOVED_TO_UPDATES.map(([from]) => ({ path: `${from.slice(1)}/*`, element: <MovedToUpdates /> })),
      ...MOVED_TO_UPDATES.map(([from]) => ({ path: from.slice(1), element: <MovedToUpdates /> })),

      {
        path: 'error-stats',
        element: <ErrorStatsRedirect />,
      },
      {
        path: 'product-crashes',
        element: <ProductCrashesRedirect />,
      },
      {
        path: 'product-errors',
        element: <ProductErrorsRedirect />,
      },

      // Analytics routes (external_viewer — spec §3.3)
      {
        path: 'analytics',
        element: (
          <RequireRole allow={['business_analyst', 'admin', 'external_viewer']}>
            <Analytics />
          </RequireRole>
        ),
      },

      // Support routes (external_viewer — spec §3.3)
      {
        path: 'support',
        element: (
          <RequireRole allow={['support_agent', 'admin', 'external_viewer']}>
            <SupportSearch />
          </RequireRole>
        ),
      },
      {
        path: 'support/diagnostics/:deviceId',
        element: (
          <RequireRole
            allow={[
              'developer',
              'admin',
              'support_agent',
              'business_analyst',
              'external_viewer',
            ]}
          >
            <DeviceDiagnostics />
          </RequireRole>
        ),
      },

      // Home routes
      {
        path: 'homes',
        element: (
          <RequireRole allow={['business_analyst', 'admin']}>
            <HomeList />
          </RequireRole>
        ),
      },
      {
        path: 'homes/:homeId',
        element: (
          <RequireRole allow={['business_analyst', 'admin']}>
            <HomeDetail />
          </RequireRole>
        ),
      },

      // Help routes (P0.3 glossary — any authenticated role, not role-gated;
      // the parent `/` route already wraps children in RequireAuth via Layout)
      {
        path: 'help/glossary',
        element: <Glossary />,
      },

      // Audit log (P0.6 — operator audit of OTA admin writes; developer + admin)
      {
        path: 'audit',
        element: (
          <RequireRole allow={['admin', 'developer']}>
            <AuditLog />
          </RequireRole>
        ),
      },

      // 404 - Not Found
      {
        path: '*',
        element: (
          <div style={{ padding: 24 }}>
            <h1>404 - Page Not Found</h1>
            <p>The page you are looking for does not exist.</p>
          </div>
        ),
      },
    ],
  },
])

